import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Edit2, Trash2, Search, Calendar, Copy } from 'lucide-react';
import toast from 'react-hot-toast';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import { COLORS } from '../../utils/constants';
import dayjs from 'dayjs';

const C = COLORS;

const PalQuizListPage = ({ title = 'Practice', quizType = 'Practice', basePath = '/pal/practice' }) => {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [activeTab, setActiveTab] = useState('Board Exams');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchQuizzes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/quizzes');
      setQuizzes(res.data);
    } catch (err) {
      toast.error(`Failed to load ${title.toLowerCase()} quizzes`);
    } finally {
      setLoading(false);
    }
  }, [title]);

  useEffect(() => {
    fetchQuizzes();
  }, [fetchQuizzes]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, dateFilter, activeTab]);

  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete this ${title.toLowerCase()} item?`)) return;
    try {
      await api.delete(`/quizzes/${id}`);
      toast.success('Deleted successfully');
      fetchQuizzes();
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const handleDuplicate = async (id, name) => {
    if (!window.confirm(`Do you want to duplicate "${name}"?`)) return;
    const toastId = toast.loading('Duplicating quiz...');
    try {
      await api.post(`/quizzes/${id}/duplicate`);
      toast.success(`"${name}" duplicated successfully!`, { id: toastId });
      fetchQuizzes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to duplicate quiz', { id: toastId });
    }
  };

  const filteredQuizzes = quizzes.filter(q => {
    // Match quiz_type (default to Practice if missing)
    const qType = q.quiz_type || 'Practice';
    if (qType !== quizType) return false;

    // Match question_type tab (Board Exams vs Compititive)
    if (q.question_type !== activeTab) return false;

    const s = search.toLowerCase();
    const matchSearch = (
      (q.id?.toString() || '').includes(s) ||
      (q.name || '').toLowerCase().includes(s) ||
      (q.board_name || '').toLowerCase().includes(s) ||
      (q.exam_name || '').toLowerCase().includes(s) ||
      (q.class_name || '').toLowerCase().includes(s) ||
      (q.subject_name || '').toLowerCase().includes(s)
    );
    const matchDate = dateFilter ? (
      dayjs(q.start_date).format('YYYY-MM-DD') === dateFilter || 
      dayjs(q.end_date).format('YYYY-MM-DD') === dateFilter
    ) : true;
    return matchSearch && matchDate;
  });

  const totalPages = Math.ceil(filteredQuizzes.length / itemsPerPage);
  const paginatedQuizzes = filteredQuizzes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <AppLayout title={title}>
      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: 24, marginBottom: 24, borderBottom: `1px solid ${C.g200}` }}>
        {['Board Exams', 'Compititive'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '12px 16px',
              border: 'none',
              background: 'none',
              fontSize: 15,
              fontWeight: activeTab === tab ? 600 : 500,
              color: activeTab === tab ? C.primary : C.g500,
              borderBottom: `2px solid ${activeTab === tab ? C.primary : 'transparent'}`,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              marginBottom: -1
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Search, Date Filter & Add Button */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ position: 'relative', width: 250 }}>
            <Search size={18} color={C.g400} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }}
            />
          </div>
          <div style={{ position: 'relative' }}>
            <Calendar size={18} color={C.g400} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{ padding: '10px 14px 10px 40px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', color: dateFilter ? C.g900 : C.g400 }}
            />
          </div>
        </div>
        <button
          onClick={() => navigate(`${basePath}/add`)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)' }}
        >
          <Plus size={18} /> Add {title}
        </button>
      </div>

      {/* Quizzes Table */}
      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: 1000 }}>
            <thead>
              <tr style={{ background: C.g50, borderBottom: `1px solid ${C.g200}` }}>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>ID</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Name</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>
                  {activeTab === 'Compititive' ? 'Exam/Class' : 'Board/Class'}
                </th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Subject</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Dates</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '14px 20px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" style={{ padding: 40, textAlign: 'center', color: C.g400 }}>Loading...</td></tr>
              ) : paginatedQuizzes.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: 40, textAlign: 'center', color: C.g500 }}>
                    No {title.toLowerCase()} items found. Click &quot;Add {title}&quot; to create one.
                  </td>
                </tr>
              ) : (
                paginatedQuizzes.map(q => (
                  <tr key={q.id} style={{ borderBottom: `1px solid ${C.g100}` }}>
                    <td style={{ padding: '16px 20px', fontSize: 14, color: C.g500 }}>#{q.id}</td>
                    <td style={{ padding: '16px 20px', fontSize: 14, fontWeight: 500, color: C.g900 }}>
                      <div>{q.name}</div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                        <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#EEF2FF', color: '#4F46E5' }}>
                          {q.quiz_type || 'Practice'}
                        </span>
                        {q.duration && (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#FEF3C7', color: '#D97706' }}>
                            ⏱ {q.duration} mins
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                      {q.question_type === 'Board Exams' ? `${q.board_name || '-'} / ` : `${q.exam_name || '-'} / `}{q.class_name || '-'}
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                      {q.subject_name || '-'}
                    </td>
                    <td style={{ padding: '16px 20px', fontSize: 13, color: C.g600 }}>
                      {dayjs(q.start_date).format('MMM D, YYYY')} - <br/>{dayjs(q.end_date).format('MMM D, YYYY')}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        background: q.status === 'active' ? '#ECFDF5' : '#FEF2F2',
                        color: q.status === 'active' ? '#059669' : '#DC2626'
                      }}>
                        {q.status.charAt(0).toUpperCase() + q.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                        <button onClick={() => navigate(`${basePath}/${q.id}`)} style={{ padding: 6, background: '#EFF6FF', border: 'none', borderRadius: 6, color: '#2563EB', cursor: 'pointer' }} title="View">
                          <Eye size={16} />
                        </button>
                        <button onClick={() => navigate(`${basePath}/edit/${q.id}`)} style={{ padding: 6, background: C.g50, border: 'none', borderRadius: 6, color: C.g600, cursor: 'pointer' }} title="Edit">
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDuplicate(q.id, q.name)} style={{ padding: 6, background: '#F5F3FF', border: 'none', borderRadius: 6, color: '#7C3AED', cursor: 'pointer' }} title="Duplicate">
                          <Copy size={16} />
                        </button>
                        <button onClick={() => handleDelete(q.id)} style={{ padding: 6, background: '#FEF2F2', border: 'none', borderRadius: 6, color: C.danger, cursor: 'pointer' }} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: `1px solid ${C.g200}`, background: '#F9FAFB' }}>
            <span style={{ fontSize: 14, color: C.g500 }}>
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredQuizzes.length)} of {filteredQuizzes.length} entries
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: '6px 12px', background: currentPage === 1 ? '#F3F4F6' : '#fff', color: currentPage === 1 ? C.g400 : C.g700, border: `1px solid ${C.g200}`, borderRadius: 6, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500 }}
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{ padding: '6px 12px', background: currentPage === totalPages ? '#F3F4F6' : '#fff', color: currentPage === totalPages ? C.g400 : C.g700, border: `1px solid ${C.g200}`, borderRadius: 6, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500 }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default PalQuizListPage;
